import React, { useReducer, useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";

import scorePlusSampleData from "./sampledata/scorePlusSampleData";

const today = new Date().toISOString().split("T")[0];

const initialState = {
    activeView:"dashboard",
    loading: false,
    sampleMode: false,
    showGraph: false,
    message: null,
    records: [],
    filteredRecords: [],
    selectedRecord: null,
    confirmDelete: null,

    form: {
        act: "",
        activity: "",
        date: today,
        status: "Done",
        startTime: "",
        duration: 0,
        durationUnit: "Min",
        tags: "",
        viewerCode1: "",
        viewerCode2: "",
        viewerCode3: ""
    },

    filters: {
        activity: "",
        fromDate: null,
        toDate: null,
        tag: "",
        viewerCode1: "",
        viewerCode2: "",
        viewerCode3: "",
        status: "",
        duration: ""
    }
};

function reducer(state, action) {
    switch (action.type) {
        case "SET_VIEW":
            return {
                ...state,
                activeView: action.payload
            };
        case "SET_FORM":
            return {
                ...state,
                form: {
                    ...state.form,
                    ...action.payload
                }
            };

        case "SET_FILTERS":
            return {
                ...state,
                filters: {
                    ...state.filters,
                    ...action.payload
                }
            };

        case "SET_FILTERED_RECORDS":
            return {
                ...state,
                filteredRecords: action.payload
            };            

        case "SET_LOADING":
            return {
                ...state,
                loading: action.payload
            };

        case "SET_MESSAGE":
            return {
                ...state,
                message: action.payload
            };

        case "CLEAR_MESSAGE":
            return {
                ...state,
                message: null
            };

        case "SET_SELECTED_RECORD":
            return {
                ...state,
                selectedRecord: action.payload
            };

        case "SET_RECORDS":
            return {
                ...state,
                records: action.payload,
                filteredRecords: action.payload
            };

        case "TOGGLE_GRAPH":
            return {
                ...state,
                showGraph: !state.showGraph
            };

        case "TOGGLE_SAMPLE":
            return {
                ...state,
                sampleMode: !state.sampleMode,
                records: [],
                filteredRecords: []
            };

        case "SET_CONFIRM_DELETE":
            return {
                ...state,
                confirmDelete: action.payload
            };

        default:
            return state;
    }
}

export default function useScorePlus(props) {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const userName = authState?.user?.name;
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (!userId) return;
        if (state.sampleMode) {
            setRecords(scorePlusSampleData);
        } else {
            loadRecords();
        }
    }, [
        userId,
        state.sampleMode
    ]);
    

    const setView = (view) => {
        dispatch({
            type:"SET_VIEW",
            payload:view
        });
    };

    const loadRecords = async () => {
        try {
            setLoading(true);
            const config = {
                url: "/scoreplus/my",
                method: "post",
                data: {
                    userId,
                    fromDate: state.filters.fromDate,
                    toDate: state.filters.toDate,
                },
                headers: {
                    Authorization: authState.token,
                },
            };

            const res = await fetchData(config, {
                silent: true,
            });

            if (res.success) {                
                dispatch({
                    type: "SET_FILTERS",
                    payload: {
                        fromDate: null,
                        toDate: null,
                        tag: "",
                        status: "",
                        duration: "",
                        viewerCode1: "",
                        viewerCode2: "",
                        viewerCode3: ""
                    }
                });
                setRecords(res.records || []);
            } else {
                setMessage(
                    "error",
                    res.message || "Record Not Found / Unable To Load Records."
                );
            }
        } catch (err) {
            console.error(err);
            setMessage(
                "error",
                "Unable to load records."
            );
        } finally {
            setLoading(false);
        }
    };

        // Improved filter application
    const applyFilters = () => {
        let result = [...state.records];

        const { fromDate, toDate, status, tag, duration, viewerCode1, viewerCode2, viewerCode3 } = state.filters;

        if (fromDate) {
            result = result.filter(r => r.date >= fromDate);
        }
        if (toDate) {
            result = result.filter(r => r.date <= toDate);
        }
        if (status) {
            result = result.filter(r => String(r.status).trim() === status);
        }
        if (viewerCode1) {
            result = result.filter(r => String(r.viewerCode1).trim() === viewerCode1);
        }
        if (viewerCode2) {
            result = result.filter(r => String(r.viewerCode2).trim() === viewerCode2);
        }
        if (viewerCode3) {
            result = result.filter(r => String(r.viewerCode3).trim() === viewerCode3);
        }
        if (tag && tag.trim() !== "") {
            const searchTags = tag
                .split(",")
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);

            result = result.filter(record => {
                const recordTags = (record.tags || [])
                    .map(t => String(t).trim().toLowerCase());

                return searchTags.every(searchTag =>
                    recordTags.some(recordTag => recordTag.includes(searchTag))
                );
            });
        }
        if (duration) {
            const d = Number(duration);
            if (d === 30) result = result.filter(r => Number(r.duration) < 30);
            else if (d === 60) result = result.filter(r => Number(r.duration) >= 30 && Number(r.duration) <= 60);
            else if (d === 180) result = result.filter(r => Number(r.duration) > 60 && Number(r.duration) <= 180);
            else if (d === 181) result = result.filter(r => Number(r.duration) > 180);
        }

        dispatch({
            type: "SET_FILTERED_RECORDS",
            payload: result
        });
    };

    // Run filters whenever records or filters change
    useEffect(() => {
        applyFilters();
    }, [state.records, state.filters]);

    const updateForm = (field, value) => {
        dispatch({
            type: "SET_FORM",
            payload: {
                [field]: value
            }
        });
    };

    const updateFilters = (field, value) => {
        dispatch({
            type: "SET_FILTERS",
            payload: {
                [field]: value
            }
        });
    };

    const setLoading = (value) => {
        dispatch({
            type: "SET_LOADING",
            payload: value
        });
    };

    const setRecords = (records) => {
        dispatch({
            type: "SET_RECORDS",
            payload: records
        });
    };

    const setMessage = (type, text) => {
        dispatch({
            type: "SET_MESSAGE",
            payload: { type, text }
        });
    };

    const clearMessage = () => {
        dispatch({
            type: "CLEAR_MESSAGE"
        });
    };

    const editRecord = (record) => {
        dispatch({
            type: "SET_SELECTED_RECORD",
            payload: record
        });

        dispatch({
            type: "SET_FORM",
            payload: {
                _id: record._id,
                act: record.act,
                activity: record.activity,
                date: record.date?.substring(0,10),
                status: record.status,
                startTime: record.startTime,
                duration: record.duration,
                durationUnit: record.durationUnit,
                tags: (record.tags || []).join(","),
                viewerCode1: record.viewerCode1,
                viewerCode2: record.viewerCode2,
                viewerCode3: record.viewerCode3
            }
        });

        dispatch({
            type: "SET_VIEW",
            payload: "form"
        });

    };

    const saveRecord = async () => {
        if (state.selectedRecord) {
            return updateRecord();
        }

        try {
            setLoading(true);

            const config = {
                url: "/scoreplus/create",
                method: "post",
                data: {
                    userId,
                    userCode,
                    userName,
                    act: state.form.act,
                    activity: state.form.activity,
                    date: state.form.date,
                    status: state.form.status,
                    startTime: state.form.startTime,
                    duration: Number(state.form.duration),
                    durationUnit: state.form.durationUnit,
                    tags: state.form.tags
                        .split(",")
                        .map(tag => tag.trim())
                        .filter(Boolean),
                    viewerCode1: state.form.viewerCode1,
                    viewerCode2: state.form.viewerCode2,
                    viewerCode3: state.form.viewerCode3
                },
                headers: {
                    Authorization: authState.token,
                },
            };

            const res = await fetchData(config, {
                silent: true,
            });

            if (res.success) {
                setMessage("success", "Record created successfully.");
                resetForm();
                loadRecords();
            } else {
                setMessage("error", res.message || "Unable to create record.");
            }

        } catch (err) {
            console.error(err);
            setMessage("error", "Unable to create record.");
        } finally {
            setLoading(false);
        }

    };

    const updateRecord = async () => {
        try {

            setLoading(true);

            const config = {
                url: "/scoreplus/update",
                method: "post",
                data: {
                    recordId: state.form._id,
                    userId,
                    userCode,
                    userName,
                    act: state.form.act,
                    activity: state.form.activity,
                    date: state.form.date,
                    status: state.form.status,
                    startTime: state.form.startTime,
                    duration: Number(state.form.duration),
                    durationUnit: state.form.durationUnit,
                    tags: state.form.tags
                        .split(",")
                        .map(tag => tag.trim())
                        .filter(Boolean),
                    viewerCode1: state.form.viewerCode1,
                    viewerCode2: state.form.viewerCode2,
                    viewerCode3: state.form.viewerCode3,
                    isActive: true,
                },
                headers: {
                    Authorization: authState.token,
                },
            };

            const res = await fetchData(config, {
                silent: true,
            });

            if (res.success) {
                setMessage("success", "Record updated successfully.");
                resetForm();
                loadRecords();
            } else {
                setMessage("error", res.message || "Unable to update record.");
            }

        } catch (err) {
            console.error(err);
            setMessage("error", "Unable to update record.");
        } finally {
            setLoading(false);
        }

    };

    const deleteRecord = async (recordId) => {
        try {
            if (!recordId) return;

            setLoading(true);

            const config = {
                url: `/scoreplus/delete/${recordId}`,
                method: "delete",
                headers: {
                    Authorization: authState.token,
                },
            };

            const res = await fetchData(config, {
                silent: true,
            });

            if (res.success) {
                dispatch({
                    type: "SET_CONFIRM_DELETE",
                    payload: null,
                });

                setMessage("success", "Record deleted successfully.");
                loadRecords();
            } else {
                setMessage("error", res.message || "Unable to delete record.");
            }
        } catch (err) {
            console.error(err);
            setMessage("error", "Unable to delete record.");
        } finally {
            setLoading(false);
        }
    };

    const toggleGraph = () => {
        dispatch({ type: "TOGGLE_GRAPH" });
    };

    const toggleSample = () => {
        dispatch({ type: "TOGGLE_SAMPLE" });

        dispatch({
            type: "SET_FILTERS",
            payload: {
                fromDate: null,
                toDate: null,
                tag: "",
                status: "",
                duration: "",
                viewerCode1: "",
                viewerCode2: "",
                viewerCode3: ""
            }
        });

        dispatch({
            type: "SET_VIEW",
            payload: "dashboard"
        });

        dispatch({
            type: "SET_SELECTED_RECORD",
            payload: null
        });

        dispatch({
            type: "SET_FORM",
            payload: initialState.form
        });
    };

    const resetForm = () => {
        dispatch({
            type: "SET_SELECTED_RECORD",
            payload: null
        });

        dispatch({
            type: "SET_FORM",
            payload: initialState.form
        });
    };

    const clearFilters = () => {
        dispatch({
        type: "SET_FILTERS",
        payload: {
            fromDate: null,
            toDate: null,
            tag: "",
            status: "",
            duration: "",
            viewerCode1: "",
            viewerCode2: "",
            viewerCode3: ""
        }
        });
    };

    const refreshData = () => {
        if (state.sampleMode) {
            setRecords(scorePlusSampleData);
        } else {
            loadRecords();
        }
    };

    return {
        ...state,
        setView,
        updateForm,
        updateFilters,
        applyFilters,
        loadRecords,
        clearMessage,
        saveRecord,
        editRecord,
        updateRecord,
        confirmDelete: state.confirmDelete,
        setConfirmDelete: (record) =>
        dispatch({
            type: "SET_CONFIRM_DELETE",
            payload: record
        }),
        deleteRecord,
        setLoading,
        setRecords,
        setMessage,
        toggleGraph,
        toggleSample,
        resetForm,
        clearFilters,
        refreshData,
    };
}